import React from 'react';
import { theme } from '../theme';
import { CustomDatePicker } from './CustomDatePicker';
import { useResponsive } from '../hooks/useResponsive';

interface DatePickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedDate: Date | null;
  onChange: (date: Date | null) => void;
  minDate: Date;
}

export const DatePickerModal: React.FC<DatePickerModalProps> = ({
  isOpen,
  onClose,
  selectedDate,
  onChange,
  minDate
}) => {
  const { isMobile } = useResponsive();
  
  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleDateSelect = (date: Date | null) => {
    onChange(date);
    onClose();
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
      onClick={handleOverlayClick}
      onKeyDown={(e) => { if (e.key === 'Escape') handleOverlayClick(e as any); }}
      role="presentation"
    >
      <div
        style={{
          backgroundColor: theme.colors.surface,
          border: `2px solid ${theme.colors.primary}`,
          borderRadius: theme.borderRadius.card,
          boxShadow: theme.colors.shadow,
          padding: isMobile ? theme.spacing.md : theme.spacing.lg,
          maxWidth: isMobile ? '95%' : '500px',
          width: isMobile ? '95%' : '90%',
          maxHeight: '90vh',
          overflow: 'auto',
          position: 'relative'
        }}
      >
        {/* Header del modal */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: theme.spacing.lg,
          paddingBottom: theme.spacing.md,
          borderBottom: `1px solid ${theme.colors.border}`
        }}>
          <h2 style={{
            color: theme.colors.primary,
            fontFamily: theme.fonts.heading,
            fontSize: isMobile ? theme.fonts.sizeSubtitle : theme.fonts.sizeTitle,
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing.sm
          }}>
            <span style={{ fontSize: '24px' }}>📅</span>
            Seleccionar Fecha
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: theme.colors.textSecondary,
              fontSize: '24px',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '32px',
              height: '32px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = theme.colors.background;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            ×
          </button>
        </div>

        {/* Calendario */}
        <CustomDatePicker
          selectedDate={selectedDate}
          onChange={handleDateSelect}
          minDate={minDate}
          onClose={onClose}
          isMobile={isMobile}
        />

        {/* Footer con botones */}
        <div style={{
          marginTop: theme.spacing.lg,
          paddingTop: theme.spacing.md,
          borderTop: `1px solid ${theme.colors.border}`,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: theme.spacing.sm
        }}>
          <button
            onClick={onClose}
            style={{
              padding: `${theme.spacing.sm} ${theme.spacing.md}`,
              backgroundColor: theme.colors.textSecondary,
              color: theme.colors.white,
              border: 'none',
              borderRadius: theme.borderRadius.button,
              fontFamily: theme.fonts.body,
              fontSize: theme.fonts.sizeBody,
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = theme.colors.text;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = theme.colors.textSecondary;
            }}
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
};
